import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';

export interface PontoEdicao {
  ponto_id: number;
  nome: string;
  descricao?: string | null;
  imagem_url?: string | null;
  ordem?: number;
}

interface Props {
  eventoId: number;
  ponto: PontoEdicao | null;
  aoFechar: () => void;
  aoSalvo: () => void;
}

// Modal de barraca: nome, descrição e imagem (via loja_salvar_ponto).
export default function ModalBarraca({ eventoId, ponto, aoFechar, aoSalvo }: Props) {
  const [nome, setNome] = useState(ponto?.nome ?? '');
  const [descricao, setDescricao] = useState(ponto?.descricao ?? '');
  const [imagemUrl, setImagemUrl] = useState(ponto?.imagem_url ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    if (!nome.trim()) {
      setErro('Dê um nome para a barraca.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_salvar_ponto', {
        p_evento: eventoId,
        p_nome: nome.trim(),
        p_ponto: ponto ? ponto.ponto_id : null,
        p_ativo: true,
        p_ordem: ponto?.ordem ?? 0,
        p_descricao: descricao.trim() || null,
        p_imagem_url: imagemUrl.trim() || null,
      });
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-[480px] flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex flex-col items-center pt-2 sm:hidden">
          <span className="h-1.5 w-10 rounded-full bg-secondary-200" />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h3 className="text-[17px] font-bold text-foreground-950">
            {ponto ? 'Editar barraca' : 'Nova barraca'}
          </h3>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-4">
          <CampoTexto
            id="loja-nome-barraca"
            name="nome"
            label="Nome"
            help="Ex.: Bar, Doces, Cozinha."
            value={nome}
            onChange={setNome}
            required
            error={erro}
            autoFocus
            placeholder="Nome da barraca"
          />

          <CampoArea
            id="loja-descricao-barraca"
            name="descricao"
            label="Descrição"
            help="Opcional. Uma frase curta sobre o que se retira aqui."
            value={descricao}
            onChange={setDescricao}
            maxLength={500}
            simples
          />

          <div>
            <label
              htmlFor="loja-imagem-barraca"
              className="block text-[14px] font-medium text-foreground-950"
            >
              Imagem (URL)
            </label>
            <p className="mt-1 text-[13px] text-foreground-500">
              Opcional. O endereço de uma imagem para ilustrar a barraca.
            </p>
            <input
              id="loja-imagem-barraca"
              name="imagem_url"
              type="url"
              value={imagemUrl}
              onChange={(e) => setImagemUrl(e.target.value)}
              placeholder="https://…"
              className="mt-2 h-12 w-full rounded-2xl border border-secondary-300 bg-white px-4 text-[16px] text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="border-t border-background-100 px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="flex min-h-[52px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            onClick={aoFechar}
            className="mx-auto mt-2 block cursor-pointer whitespace-nowrap text-[14px] font-medium text-foreground-500 transition-colors hover:text-foreground-800"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}